import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type NotifCategory =
  | "deposit"
  | "withdrawal"
  | "referral"
  | "account"
  | "announcement";

export type NotifChannelPrefs = { in_app: boolean; email: boolean };
export type NotifPrefs = Partial<Record<NotifCategory, NotifChannelPrefs>>;

export const NOTIF_CATEGORIES: {
  key: NotifCategory;
  label: string;
  description: string;
  types: string[];
}[] = [
  {
    key: "deposit",
    label: "Deposits",
    description: "When a deposit is submitted, approved, or rejected.",
    types: ["deposit_pending", "deposit_approved", "deposit_rejected"],
  },
  {
    key: "withdrawal",
    label: "Withdrawals",
    description: "Withdrawal requests and admin decisions.",
    types: ["withdrawal_pending", "withdrawal_approved", "withdrawal_rejected"],
  },
  {
    key: "referral",
    label: "Referral commissions",
    description: "Earnings credited from referred users.",
    types: ["referral_earned"],
  },
  {
    key: "account",
    label: "Account & security",
    description: "Welcome, email verification, password changes.",
    types: ["welcome", "email_verified", "password_changed"],
  },
  {
    key: "announcement",
    label: "Platform announcements",
    description: "Product updates and important news.",
    types: ["announcement"],
  },
];

export function categoryOf(type: string | null | undefined): NotifCategory {
  if (!type) return "account";
  for (const c of NOTIF_CATEGORIES) if (c.types.includes(type)) return c.key;
  return "account";
}

const DEFAULT: NotifChannelPrefs = { in_app: true, email: true };

export function channelPrefsFor(
  prefs: NotifPrefs | null | undefined,
  category: NotifCategory,
): NotifChannelPrefs {
  return { ...DEFAULT, ...(prefs?.[category] ?? {}) };
}

export function isInAppEnabled(prefs: NotifPrefs | null | undefined, type: string) {
  return channelPrefsFor(prefs, categoryOf(type)).in_app;
}

export function useNotificationPrefs() {
  return useQuery({
    queryKey: ["notification_prefs"],
    queryFn: async (): Promise<NotifPrefs> => {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) return {};
      const { data } = await supabase
        .from("notification_preferences")
        .select("prefs")
        .eq("user_id", uid)
        .maybeSingle();
      return (data?.prefs as NotifPrefs) ?? {};
    },
    staleTime: 60_000,
  });
}